import { useEffect, useState } from "react";
import { useAppSelector } from "state/hooks";
import { useAuth } from "hooks/auth";
import { getUserState } from "state/slices/user-slice";
import {
  getIntegratedAppState,
  IntegratedAppState,
} from "state/slices/integrated-app-slice";
import { USERNAME_CUSTOMISATION_POPUP_LIST } from "utils/constants";
import { local as localStorage } from "utils/local-storage";
import UsernameForm from "./index";

interface Props {
  // Called after the user closes/skips the modal. Lets the host page chain
  // follow-up prompts (e.g. Home opens the join-group modal next).
  onClose?: () => void;
}

// Owns the "Please Choose a Username" popup that greets a freshly registered
// user. Extracted out of Home so it can be mounted on any home page (regular
// Home + contest ContestHome) and behave identically. Only fires for the
// backend-generated `player<n>` username, once per username, and only in the
// non-integrated flow.
const UsernamePrompt = ({ onClose }: Props) => {
  const auth = useAuth();
  const user = useAppSelector(getUserState);
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  useEffect(() => {
    if (
      !auth.signedIn ||
      integratedAppState.state === IntegratedAppState.UNDEFINED
    ) {
      return;
    }

    const username = user.user_name;

    const sparketGeneratedUsername =
      username.substr(0, 6) === "player" && !isNaN(Number(username.substr(6)));

    const alreadyAskedUsernames =
      localStorage.getItem(USERNAME_CUSTOMISATION_POPUP_LIST)?.split(",") || [];
    if (sparketGeneratedUsername && !alreadyAskedUsernames.includes(username)) {
      setShowUsernameModal(true);
    }
  }, [integratedAppState.state, auth.signedIn, user.user_name]);

  if (
    !auth.signedIn ||
    integratedAppState.state !== IntegratedAppState.NOT_INTEGRATED
  ) {
    return null;
  }

  return (
    <UsernameForm
      close={() => {
        const alreadyAskedUsernames =
          localStorage
            .getItem(USERNAME_CUSTOMISATION_POPUP_LIST)
            ?.split(",") || [];
        alreadyAskedUsernames.push(user.user_name);

        localStorage.setItem(
          USERNAME_CUSTOMISATION_POPUP_LIST,
          alreadyAskedUsernames.join(",")
        );

        setShowUsernameModal(false);

        onClose?.();
      }}
      show={showUsernameModal}
    />
  );
};

export default UsernamePrompt;
